import { enhancedApi as eligibilityGeneratedApi } from '@store/generated/eligibility';
import {
  enhancedApi,
  type InsuranceCardExtractionResponseSchema,
} from '@store/generated/patient-coverage';

/**
 * Response from the insurance card extraction endpoint.
 *
 * The upload is multipart, so the mutation below is hand-injected
 * rather than generated — but the response shape is not. It comes
 * straight from the OpenAPI contract.
 *
 * As of the GDN-1032 follow-up, the endpoint also runs the file through
 * the standard artifact pipeline AND auto-creates the coverage row in
 * the same request, so the response carries:
 *   - `artifact_ids` for the underlying Artifact rows (deep-linkable
 *     into the unified documents UI)
 *   - `coverage_id` for the newly-created PatientCoverage row, OR null
 *     if creation was skipped (e.g. patient already has a primary
 *     coverage and the BE refused to overwrite). Null means the
 *     provider should manually save the prefilled form.
 */
export type InsuranceCardExtractionResponse = InsuranceCardExtractionResponseSchema;

/**
 * Feature-specific tag types for cache management
 */
const PATIENT_COVERAGE_TAG_TYPES = [
  'PatientCoverage',
  'PatientCoverageList',
  'PayerStats',
  'Worklist',
  'Eligibility',
  'AppointmentsList',
  'NextAppointment',
  'PatientPageV3Bootstrap',
  // Declared by features/patients (GDN-2730). Re-declared here so the card
  // upload — the only thing that creates a proposal — can invalidate it.
  'PatientSuggestedActions',
  // Declared by features/billing. Re-declared here so a coverage write can
  // invalidate the unified encounter worklist: a row with no claim resolves its
  // payer from the coverage in force on the date of service, so editing
  // insurance changes what that row should say. 'Worklist' above is a
  // different tag type and does not cover it.
  'BillingEncounter',
  'BillingEncountersList',
] as const;

type CoverageWriteInvalidationTags = (
  | { type: 'PatientCoverage'; id: string }
  | { type: 'PatientCoverageList'; id: string }
  | 'PayerStats'
  | 'Worklist'
  | 'Eligibility'
  | { type: 'Eligibility'; id: string }
  | { type: 'AppointmentsList'; id: 'LIST' }
  | { type: 'NextAppointment'; id: 'NEXT' }
  | { type: 'PatientPageV3Bootstrap'; id: string }
  | { type: 'BillingEncountersList'; id: 'UNIFIED_WORKLIST' }
)[];

export function coverageWriteInvalidationTags(
  patientId: string,
  coverageId?: string | null,
): CoverageWriteInvalidationTags {
  return [
    ...(coverageId ? [{ type: 'PatientCoverage' as const, id: coverageId }] : []),
    { type: 'PatientCoverageList', id: patientId },
    'PayerStats',
    'Worklist',
    'Eligibility',
    { type: 'Eligibility', id: `patient-${patientId}` },
    { type: 'AppointmentsList', id: 'LIST' },
    { type: 'NextAppointment', id: 'NEXT' },
    { type: 'PatientPageV3Bootstrap', id: patientId },
    { type: 'BillingEncountersList', id: 'UNIFIED_WORKLIST' },
  ];
}

/**
 * Patient Coverages API - extends the generated OpenAPI client
 * This ensures we're using generated types and endpoints
 * while maintaining feature-specific customization
 */
export const patientCoveragesApi = enhancedApi.enhanceEndpoints({
  addTagTypes: PATIENT_COVERAGE_TAG_TYPES,
  endpoints: {
    // Payer stats (org-wide summary)
    getV2ProviderPatientsPayerStats: {
      providesTags: ['PayerStats'],
    },

    // Enhance generated endpoints with cache tags
    getV2ProviderPatientsByPatientIdCoverages: {
      providesTags: (result, _error, arg) =>
        result
          ? [
              ...result.coverages.map(coverage => ({
                type: 'PatientCoverage' as const,
                id: coverage.patient_insurance_coverage_id,
              })),
              { type: 'PatientCoverageList' as const, id: arg.patientId },
            ]
          : [{ type: 'PatientCoverageList', id: arg.patientId }],
    },

    postV2ProviderPatientsByPatientIdCoverages: {
      invalidatesTags: (_result, _error, arg) => coverageWriteInvalidationTags(arg.patientId),
    },

    getV2ProviderPatientsByPatientIdCoveragesAndCoverageId: {
      providesTags: (_result, _error, arg) => [{ type: 'PatientCoverage', id: arg.coverageId }],
    },

    putV2ProviderPatientsByPatientIdCoveragesAndCoverageId: {
      invalidatesTags: (_result, _error, arg) =>
        coverageWriteInvalidationTags(arg.patientId, arg.coverageId),
    },

    deleteV2ProviderPatientsByPatientIdCoveragesAndCoverageId: {
      invalidatesTags: (_result, _error, arg) =>
        coverageWriteInvalidationTags(arg.patientId, arg.coverageId),
    },
  },
});

// Custom (non-generated) endpoints for insurance card upload
const insuranceCardApi = patientCoveragesApi.injectEndpoints({
  endpoints: builder => ({
    uploadInsuranceCard: builder.mutation<
      InsuranceCardExtractionResponse,
      { patientId: string; files: File[] }
    >({
      query: ({ patientId, files }) => {
        const formData = new FormData();
        files.forEach(file => formData.append('files[]', file, file.name));
        return {
          url: `/v2/provider/patients/${patientId}/insurance-card`,
          method: 'POST',
          body: formData,
        };
      },
      // The endpoint now auto-creates a PatientCoverage row in the same
      // request — invalidate the patient's coverage list so the form
      // re-hydrates with the new server-side coverage_id (without it,
      // a follow-up edit would create a duplicate via POST instead of
      // updating via PUT).
      //
      // Under the review gate it creates a proposal instead, so the
      // pending-proposal read is invalidated too (GDN-2730). The upload
      // happens several layers below the section that renders the
      // proposal card, so without this the card only appears on a manual
      // reload. Invalidating covers both shapes — only one exists per
      // response, and refetching the other is a no-op.
      invalidatesTags: (_result, _error, arg) => [
        ...coverageWriteInvalidationTags(arg.patientId),
        { type: 'PatientSuggestedActions' as const, id: arg.patientId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const { useUploadInsuranceCardMutation } = insuranceCardApi;

// Export ONLY user-friendly hook names (never export verbose names)
export const {
  useGetV2ProviderPatientsPayerStatsQuery: useGetPayerStatsQuery,
  useGetV2ProviderPatientsByPatientIdCoveragesQuery: useGetPatientCoveragesQuery,
  useLazyGetV2ProviderPatientsByPatientIdCoveragesQuery: useLazyGetPatientCoveragesQuery,
  usePostV2ProviderPatientsByPatientIdCoveragesMutation: useCreatePatientCoverageMutation,
  useGetV2ProviderPatientsByPatientIdCoveragesAndCoverageIdQuery: useGetPatientCoverageByIdQuery,
  useLazyGetV2ProviderPatientsByPatientIdCoveragesAndCoverageIdQuery:
    useLazyGetPatientCoverageByIdQuery,
  usePutV2ProviderPatientsByPatientIdCoveragesAndCoverageIdMutation:
    useUpdatePatientCoverageMutation,
  useDeleteV2ProviderPatientsByPatientIdCoveragesAndCoverageIdMutation:
    useDeletePatientCoverageMutation,
} = patientCoveragesApi;

// ── Eligibility API (generated from OpenAPI) ──────────────────────────

export const eligibilityApi = eligibilityGeneratedApi.enhanceEndpoints({
  addTagTypes: [
    'Eligibility',
    'BatchProgress',
    'Verifications',
    'Worklist',
    'PatientPageV3Bootstrap',
  ],
  endpoints: {
    // Multi-patient worklist (GDN-1321 BE / GDN-1338 FE)
    getV2ProviderEligibilityWorklist: {
      providesTags: ['Worklist'],
    },
    // Encounter-scoped
    getV2ProviderEncountersByEncounterIdEligibility: {
      providesTags: (_result, _error, { encounterId }) => [
        { type: 'Eligibility' as const, id: encounterId },
      ],
    },
    postV2ProviderEncountersByEncounterIdEligibility: {
      // The run-check response is the same merged shape as the GET
      // (both EligibilityCheckResponse), so prime both encounter- and
      // patient-scoped caches with the result instead of round-tripping
      // through invalidation.
      async onQueryStarted({ encounterId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            eligibilityApi.util.upsertQueryData(
              'getV2ProviderEncountersByEncounterIdEligibility',
              { encounterId },
              data,
            ),
          );
          if (data.patient_id) {
            dispatch(
              eligibilityApi.util.upsertQueryData(
                'getV2ProviderPatientsByPatientIdEligibility',
                { patientId: data.patient_id },
                data,
              ),
            );
          }
        } catch {
          // mutation failed — leave cache untouched
        }
      },
      invalidatesTags: result => [
        'Worklist',
        ...(result?.patient_id
          ? [{ type: 'PatientPageV3Bootstrap' as const, id: result.patient_id }]
          : []),
      ],
    },
    postV2ProviderEncountersByEncounterIdEligibilityVerifications: {
      // Logging a verification creates a new manual snapshot. Response is the
      // merged EligibilityCheckResponse — prime caches the same way run-check
      // does, then invalidate the verification audit log.
      async onQueryStarted({ encounterId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            eligibilityApi.util.upsertQueryData(
              'getV2ProviderEncountersByEncounterIdEligibility',
              { encounterId },
              data,
            ),
          );
          if (data.patient_id) {
            dispatch(
              eligibilityApi.util.upsertQueryData(
                'getV2ProviderPatientsByPatientIdEligibility',
                { patientId: data.patient_id },
                data,
              ),
            );
          }
        } catch {
          // mutation failed — leave cache untouched
        }
      },
      invalidatesTags: ['Verifications', 'Worklist'],
    },
    postV2ProviderPatientsByPatientIdEligibilityVerifications: {
      // Patient-scoped fallback for verifications logged outside the context
      // of an upcoming visit. Same merged response shape — prime the
      // patient-scoped cache and invalidate the audit log.
      async onQueryStarted({ patientId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            eligibilityApi.util.upsertQueryData(
              'getV2ProviderPatientsByPatientIdEligibility',
              { patientId },
              data,
            ),
          );
          if (data.pinned_encounter_id) {
            dispatch(
              eligibilityApi.util.upsertQueryData(
                'getV2ProviderEncountersByEncounterIdEligibility',
                { encounterId: data.pinned_encounter_id },
                data,
              ),
            );
          }
        } catch {
          // mutation failed — leave cache untouched
        }
      },
      invalidatesTags: ['Verifications', 'Worklist'],
    },
    // Patient-scoped
    getV2ProviderPatientsByPatientIdEligibility: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'Eligibility' as const, id: `patient-${patientId}` },
      ],
    },
    postV2ProviderPatientsByPatientIdEligibility: {
      async onQueryStarted({ patientId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            eligibilityApi.util.upsertQueryData(
              'getV2ProviderPatientsByPatientIdEligibility',
              { patientId },
              data,
            ),
          );
          if (data.pinned_encounter_id) {
            dispatch(
              eligibilityApi.util.upsertQueryData(
                'getV2ProviderEncountersByEncounterIdEligibility',
                { encounterId: data.pinned_encounter_id },
                data,
              ),
            );
          }
        } catch {
          // mutation failed — leave cache untouched
        }
      },
      invalidatesTags: (_result, _error, { patientId }) => [
        'Worklist',
        { type: 'PatientPageV3Bootstrap', id: patientId },
      ],
    },
    // Disposition (silencing) — mutations all return the merged response
    postV2ProviderPatientsByPatientIdEligibilityDispositionSnooze: {
      async onQueryStarted({ patientId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            eligibilityApi.util.upsertQueryData(
              'getV2ProviderPatientsByPatientIdEligibility',
              { patientId },
              data,
            ),
          );
        } catch {
          /* leave cache untouched */
        }
      },
      invalidatesTags: ['Worklist'],
    },
    postV2ProviderPatientsByPatientIdEligibilityDispositionDismiss: {
      async onQueryStarted({ patientId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            eligibilityApi.util.upsertQueryData(
              'getV2ProviderPatientsByPatientIdEligibility',
              { patientId },
              data,
            ),
          );
        } catch {
          /* leave cache untouched */
        }
      },
      invalidatesTags: ['Worklist'],
    },
    deleteV2ProviderPatientsByPatientIdEligibilityDisposition: {
      async onQueryStarted({ patientId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            eligibilityApi.util.upsertQueryData(
              'getV2ProviderPatientsByPatientIdEligibility',
              { patientId },
              data,
            ),
          );
        } catch {
          /* leave cache untouched */
        }
      },
      invalidatesTags: ['Worklist'],
    },
    // Verification audit log
    getV2ProviderPatientsByPatientIdEligibilityVerifications: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'Verifications' as const, id: `patient-${patientId}` },
      ],
    },
    // Batch eligibility (legacy scheduled batch)
    postV2ProviderEligibilityBatch: {
      invalidatesTags: ['BatchProgress', 'Worklist'],
    },
    getV2ProviderEligibilityBatchByBatchId: {
      providesTags: (_result, _error, { batchId }) => [
        { type: 'BatchProgress' as const, id: batchId },
      ],
    },
    // Bulk check (sync, up to 25 targets per call) — used by the
    // eligibility-checks tab's "Run all" / "Run selected" actions.
    // Invalidates the worklist so rows that just picked up a readiness
    // move out of `awaiting_check` into the appropriate segment.
    postV2ProviderEligibilityChecksBatch: {
      invalidatesTags: ['Worklist', 'Eligibility'],
    },
  },
});

export const {
  // Encounter-scoped
  useGetV2ProviderEncountersByEncounterIdEligibilityQuery: useGetEncounterEligibilityQuery,
  usePostV2ProviderEncountersByEncounterIdEligibilityMutation: useRunEligibilityCheckMutation,
  usePostV2ProviderEncountersByEncounterIdEligibilityVerificationsMutation:
    useLogVerificationMutation,
  usePostV2ProviderPatientsByPatientIdEligibilityVerificationsMutation:
    useLogPatientVerificationMutation,
  // Patient-scoped
  useGetV2ProviderPatientsByPatientIdEligibilityQuery: useGetPatientEligibilityQuery,
  usePostV2ProviderPatientsByPatientIdEligibilityMutation: useRunPatientEligibilityCheckMutation,
  // Disposition (silencing)
  usePostV2ProviderPatientsByPatientIdEligibilityDispositionSnoozeMutation:
    useSnoozeEligibilityAlertsMutation,
  usePostV2ProviderPatientsByPatientIdEligibilityDispositionDismissMutation:
    useDismissEligibilityAlertsMutation,
  useDeleteV2ProviderPatientsByPatientIdEligibilityDispositionMutation:
    useResumeEligibilityAlertsMutation,
  // Verification audit log
  useGetV2ProviderPatientsByPatientIdEligibilityVerificationsQuery: useGetVerificationHistoryQuery,
  // Worklist (multi-patient)
  useGetV2ProviderEligibilityWorklistQuery: useGetEligibilityWorklistQuery,
  // Batch eligibility (legacy scheduled batch)
  usePostV2ProviderEligibilityBatchMutation: useRunBatchEligibilityMutation,
  useGetV2ProviderEligibilityBatchByBatchIdQuery: useGetBatchProgressQuery,
  // Bulk eligibility check (sync, up to 25 targets per call)
  usePostV2ProviderEligibilityChecksBatchMutation: useRunBulkEligibilityChecksMutation,
} = eligibilityApi;
